import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Plus } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import { formatINR, resolveImage } from '@/lib/format';
import { toast } from 'sonner';

const MotionLink = motion.create(Link);

export default function ProductCard({ product, index = 0 }) {
    const { addItem } = useCart();
    const reduce = useReducedMotion();

    const onAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        addItem(product, 1);
        toast.success(`${product.name} added to cart`);
    };

    const discount = product.compare_at_price && product.compare_at_price > product.price
        ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
        : 0;

    return (
        <MotionLink
            to={`/product/${product.slug}`}
            data-testid="product-card"
            className="group card-premium relative flex flex-col overflow-hidden focus-ring will-change-transform"
            whileHover={reduce ? undefined : { y: -8 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        >
            <div className="relative aspect-[4/5] product-stage overflow-hidden">
                {/* hover glow */}
                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(120%_90%_at_50%_120%,rgba(254,235,2,0.20),transparent_60%)]" />
                <img
                    src={resolveImage(product.image)}
                    alt={product.name}
                    loading="lazy"
                    decoding="async"
                    className="relative h-full w-full object-contain p-5 transition-transform duration-500 ease-out group-hover:scale-[1.09] group-hover:-rotate-1"
                />
                <div className="absolute left-3 top-3 flex flex-col gap-1.5">
                    {product.badge && (
                        <span className="rounded-full bg-[var(--xtra-yellow)] text-black text-[11px] font-bold px-2.5 py-1 shadow-sm">{product.badge}</span>
                    )}
                    {discount > 0 && (
                        <span className="rounded-full bg-[var(--xtra-blue)] text-white text-[11px] font-bold px-2.5 py-1">-{discount}%</span>
                    )}
                </div>
                {/* Quick add: always-visible circular button (reliable on touch + desktop) */}
                <button
                    onClick={onAdd}
                    data-testid="product-card-quick-add-button"
                    aria-label={`Add ${product.name} to cart`}
                    className="absolute right-3 bottom-3 h-11 w-11 rounded-full bg-[var(--xtra-blue)] text-white grid place-items-center shadow-[var(--shadow-blue)] hover:bg-[var(--xtra-blue-ink)] hover:scale-110 active:scale-95 transition-[transform,background-color] duration-200 focus-ring group-hover:translate-y-0 translate-y-0"
                >
                    <Plus className="h-5 w-5" />
                </button>
            </div>
            <div className="flex flex-1 flex-col p-4">
                <div className="flex items-center gap-1 text-xs text-[var(--text-muted)]">
                    <Star className="h-3.5 w-3.5 fill-[var(--xtra-yellow)] text-[var(--xtra-yellow)]" />
                    <span className="font-semibold text-black tabular-nums">{product.rating}</span>
                    <span>({product.reviews_count})</span>
                    <span className="mx-1">·</span>
                    <span>{product.size}</span>
                </div>
                <h3 data-testid="product-card-title" className="font-display font-bold text-[15px] text-black mt-1.5 leading-snug">{product.name}</h3>
                <p className="text-sm text-[var(--text-muted)] mt-1 line-clamp-2 flex-1">{product.tagline}</p>
                <div className="mt-3 flex items-end justify-between">
                    <div className="flex items-baseline gap-2">
                        <span data-testid="product-card-price" className="font-display text-lg font-extrabold text-black tabular-nums">{formatINR(product.price)}</span>
                        {discount > 0 && (
                            <span className="text-sm text-[var(--text-muted)] line-through tabular-nums">{formatINR(product.compare_at_price)}</span>
                        )}
                    </div>
                </div>
            </div>
        </MotionLink>
    );
}
